/**
 * Deficiency Helper Utilities
 *
 * Shared utilities for handling deficiency data in report rows
 */

/**
 * Check if a report row has a valid deficiency
 * Handles both string IDs and populated deficiency objects
 *
 * @param {string|object} deficiencyId - The deficiency ID or populated object
 * @returns {boolean} - True if valid deficiency exists
 */
export const hasDeficiency = (deficiencyId) => {
  try {
    if (!deficiencyId) {
      return false;
    }

    if (typeof deficiencyId === 'string') {
      return deficiencyId.trim() !== '';
    }

    if (typeof deficiencyId === 'object' && deficiencyId._id) {
      return (
        typeof deficiencyId._id === 'string' && deficiencyId._id.trim() !== ''
      );
    }

    console.warn(
      '[deficiencyHelpers] Invalid deficiencyId format:',
      deficiencyId
    );
    return false;
  } catch (err) {
    console.error('[deficiencyHelpers] Error checking deficiency:', err);
    return false;
  }
};

/**
 * Get a deficiency object suitable for the DeficiencyIcon component
 * Converts string IDs to minimal objects with _id field
 *
 * @param {string|object} deficiencyId - The deficiency ID or populated object
 * @returns {object|null} - Deficiency object with at least _id field, or null
 */
export const getDeficiencyForIcon = (deficiencyId) => {
  try {
    if (!deficiencyId) {
      return null;
    }

    if (typeof deficiencyId === 'string') {
      return { _id: deficiencyId };
    }

    if (typeof deficiencyId === 'object' && deficiencyId._id) {
      return deficiencyId;
    }

    console.warn(
      '[deficiencyHelpers] Cannot create deficiency object from:',
      deficiencyId
    );
    return null;
  } catch (err) {
    console.error('[deficiencyHelpers] Error creating deficiency object:', err);
    return null;
  }
};

/**
 * The three report targets a deficiency links to.
 */
export const DEFICIENCY_REPORT_TARGETS = {
  INSPECTION: 'Inspection',
  QUOTE: 'Quote',
  WORK_ORDER: 'Work Order',
};

/**
 * Extract the deficiency id from a row's `deficiencyId`, which may be a populated
 * object (Quote/Work Order rows) or a bare id string (Inspection rows, where the
 * backend attaches the reverse link and Mongoose casts it to the ObjectId).
 * @param {string|object} deficiencyId
 * @returns {string|null}
 */
export const getDeficiencyIdValue = (deficiencyId) => {
  if (!deficiencyId) return null;
  if (typeof deficiencyId === 'string') return deficiencyId.trim() || null;
  if (typeof deficiencyId === 'object' && deficiencyId._id) {
    return String(deficiencyId._id);
  }
  return null;
};

const REPORT_TYPE_LABELS = {
  [DEFICIENCY_REPORT_TARGETS.INSPECTION]: 'View Report',
  [DEFICIENCY_REPORT_TARGETS.QUOTE]: 'View Quote',
  [DEFICIENCY_REPORT_TARGETS.WORK_ORDER]: 'View Work Order',
};

/**
 * Get a report row's ready PDF url, or null if it has no viewable PDF yet.
 * "Ready" mirrors the download-icon logic: a non-empty url that is not pending.
 * @param {object} row - A report row from the reports list
 * @returns {string|null}
 */
export const getRowPdfUrl = (row) => {
  if (!row) return null;
  const details = row.reportPDFDetails || {};
  if (details.isPending) return null;
  const url = details.url || row.pdfUrl || null;
  return typeof url === 'string' && url.trim() !== '' ? url : null;
};

/**
 * Derive the deficiency-link dropdown options for a row from its sibling rows in
 * the same report list. Siblings share a `deficiencyId`; the options are the
 * siblings of a different `reportType` whose PDF is ready (openable in a new tab).
 *
 * This is computed purely on the client from the current list data, so the PDF
 * status poller — which refreshes each row's `reportPDFDetails` in place — makes
 * a "View <type>" option appear as soon as that sibling's PDF finishes generating.
 *
 * @param {object} row - The current report row
 * @param {Array<object>} allRows - All rows currently in the report list
 * @returns {Array<{ key: string, label: string, url: string }>}
 */
export const deriveDeficiencyReportOptions = (row, allRows) => {
  try {
    if (!row || !Array.isArray(allRows)) return [];
    const defId = getDeficiencyIdValue(row.deficiencyId);
    if (!defId) return [];

    return allRows
      .filter(
        (sibling) =>
          sibling &&
          sibling._id !== row._id &&
          sibling.reportType !== row.reportType &&
          getDeficiencyIdValue(sibling.deficiencyId) === defId &&
          getRowPdfUrl(sibling) &&
          REPORT_TYPE_LABELS[sibling.reportType]
      )
      .map((sibling) => ({
        key: sibling.reportType,
        label: REPORT_TYPE_LABELS[sibling.reportType],
        url: getRowPdfUrl(sibling),
      }));
  } catch (err) {
    console.error('[deficiencyHelpers] Error deriving report options:', err);
    return [];
  }
};
